import { useEffect, useState } from 'react';

/**
 * How much of an element's bottom edge the soft keyboard is covering, in px.
 *
 * A bar pinned to the bottom of a full-screen overlay is under the keyboard the moment one
 * opens: `env(safe-area-inset-bottom)` says nothing about the keyboard, and `dvh` tracks
 * browser chrome, not the keyboard. `visualViewport` is the one thing that knows — it reports
 * the area actually visible, so the keyboard's top edge is `offsetTop + height`, and what the
 * keyboard is covering is however far the element's own bottom edge is below that.
 *
 * Measuring against the ELEMENT rather than `innerHeight` is what keeps this honest
 * everywhere, with nothing assumed about the page around it:
 *
 * - The composer's overlay stops short of the home indicator (`.safe-inset`), and the keyboard
 *   covers that strip too — measuring the real edge spends that part of the inset without
 *   anyone having to subtract `env(safe-area-inset-bottom)`. Which is just as well: Chrome
 *   resolves `env()` once and never again, so a `calc()` mixing it with a custom property
 *   silently freezes at whatever it first computed.
 * - On a desktop frame the card's edge is nowhere near the bottom of the window, so this is 0
 *   and the bar stays put.
 * - **The native shell needs no help at all.** Capacitor is configured `resize: 'native'`
 *   (capacitor.config.ts), which shrinks the WebView FRAME — a `fixed inset-0` overlay already
 *   ends at the top of the keyboard, and both edges move together, so this stays 0. Adding
 *   `@capacitor/keyboard`'s `keyboardWillShow` height on top (the way {@link useViewportLock}
 *   does, where it is measuring something else) would lift the bar a second keyboard's worth
 *   and then drop it as the frame settled: one visible wobble per tap.
 *
 * `offsetTop` is in there for iOS, which answers a focus inside a fixed element by scrolling
 * the visual viewport instead of shrinking it. Client rects and `offsetTop` are both in layout
 * coordinates, so the subtraction holds through that.
 *
 * Android Chrome collapses its URL bar as you scroll; anything under THRESHOLD is read as
 * chrome rather than a keyboard.
 */
const THRESHOLD = 90;

export function coveredBelow(el, vv = typeof window !== 'undefined' ? window.visualViewport : null) {
  if (!vv) return 0;
  const edge = el?.getBoundingClientRect ? el.getBoundingClientRect().bottom : window.innerHeight;
  const covered = edge - ((vv.offsetTop || 0) + vv.height);
  return covered > THRESHOLD ? Math.round(covered) : 0;
}

/**
 * @param {boolean} active  measure only while it matters (the field is being edited)
 * @param {{current: HTMLElement}} [ref]  the element whose bottom edge is at stake; the window's
 *                                        own bottom when it isn't given
 */
export default function useKeyboardInset(active = true, ref = null) {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!active) { setInset(0); return undefined; }

    const vv = window.visualViewport;
    // No visualViewport (an old browser) — the bar stays where it always was.
    if (!vv) return undefined;

    const sync = () => setInset(coveredBelow(ref?.current, vv));
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);   // iOS offsets rather than resizes
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, [active, ref]);

  return inset;
}
